// Shared visual mapping for RSVP states. Used by RsvpBadge, RsvpSummaryCell,
// and any future surface that renders attendance state. Single source of truth.
//
// Status union is `AttendanceStatus` from boilerplate-models.d.ts

import dayjs from 'dayjs';

import { ATTENDANCE_REASON_OPTIONS } from './attendance.constants';

export interface IRsvpStateStyle {
  // Direct hex values keyed off Material-UI v4 default palette equivalents.
  // Matches the Ionic --ion-color-{success,warning,danger} tokens used elsewhere.
  color: string;
}

export const RSVP_STYLES: Record<AttendanceStatus, IRsvpStateStyle> = {
  attending: { color: '#10dc60' },
  pending: { color: '#ffce00' },
  not_attending: { color: '#f04141' },
};

// Display label for a stored not-attending reason; `null` for an absent/unknown
// reason so callers can omit the line entirely.
export const getAttendanceReasonLabel = (reason?: AttendanceReason | null): string | null => {
  if (!reason) return null;
  const match = ATTENDANCE_REASON_OPTIONS.find((option) => option.value === reason);
  return match ? match.label : null;
};

// `(playerAssessmentId) => AttendanceStatus` from a session's responses. A missing row is `pending`:
// the backend draws no line between "no response" and a pending one, so the UI mustn't either.
export const buildRsvpLookup = (
  responses: IAttendanceResponse[] | undefined,
): ((playerAssessmentId: string) => AttendanceStatus) => {
  const map: Record<string, AttendanceStatus> = {};
  (responses || []).forEach((response) => {
    map[response.playerAssessmentId] = response.status;
  });
  return (playerAssessmentId) => map[playerAssessmentId] || 'pending';
};

// Like buildRsvpLookup but returns the full response (so callers can read the
// not-attending reason). Missing players resolve to `undefined`.
export const buildRsvpResponseLookup = (
  responses: IAttendanceResponse[] | undefined,
): ((playerAssessmentId: string) => IAttendanceResponse | undefined) => {
  const map: Record<string, IAttendanceResponse> = {};
  (responses || []).forEach((response) => {
    map[response.playerAssessmentId] = response;
  });
  return (playerAssessmentId) => map[playerAssessmentId];
};

// Minimal RSVP shape the reason/responder helpers read — satisfied by both
// IAttendanceResponse (staff views) and IPlayerAttendanceSession (parent view).
export interface IRsvpDisplayFields {
  status: AttendanceStatus;
  reason?: AttendanceReason;
  reasonNote?: string;
  respondedByName?: string;
}

// Human-readable absence reason for a not-attending response: the free-text
// note for "other", otherwise the option label. `null` for any other status.
export const getRsvpReasonText = (response: IRsvpDisplayFields | undefined): string | null => {
  if (!response || response.status !== 'not_attending') return null;
  if (response.reason === 'other') return response.reasonNote || getAttendanceReasonLabel('other');
  return getAttendanceReasonLabel(response.reason);
};

// "by <name>" caption naming who submitted the RSVP; `null` when unknown.
export const getRsvpResponderText = (response: IRsvpDisplayFields | undefined): string | null =>
  (response && response.respondedByName ? `by ${response.respondedByName}` : null);

// The validated attended/total pair, or `null` when either is missing or when
// `attended > total`. See "Data invariants — guard at the render boundary" in CLAUDE.md.
export const validateAttendanceRatio = (
  attended: number | undefined,
  total: number | undefined,
  context?: string,
): { attended: number; total: number } | null => {
  if (typeof attended !== 'number' || typeof total !== 'number') return null;
  if (attended > total) {
    if (context) {
      // eslint-disable-next-line no-console
      console.warn(`${context}: attended (${attended}) > total (${total}) — possible data corruption`);
    }
    return null;
  }
  return { attended, total };
};

// Whether the start instant (`date` + `start` minutes) has passed. A future ice time is neutral, not
// missed, so attended/missed columns must check this first. `now` is injectable for tests.
export const hasSessionOccurred = (
  session: Pick<IAssessmentSession, 'date' | 'start'>,
  now: dayjs.Dayjs = dayjs(),
): boolean => {
  if (!session.date) return false;
  const start = dayjs(session.date).add(session.start || 0, 'minute');
  return start.isValid() && start.isBefore(now);
};

// --- Public RSVP link landing state ----------------------------------------

// `pending` means "no decision yet", so driving the picker off this list rather
// than every AttendanceStatus keeps it unselectable by construction.
export const SUBMITTABLE_RSVP_STATUSES: AttendanceStatus[] = ['attending', 'not_attending'];

export type RsvpLinkState = 'malformed' | 'confirm' | 'pick';

// Collapses a public RSVP link's query params into one render state; a `pending`
// or unknown `status` resolves to 'pick', so the page never pre-selects a choice.
export const resolveRsvpLinkState = ({
  token,
  hasParentAuth = false,
  assessmentSessionId,
  status,
}: {
  token?: string | null;
  hasParentAuth?: boolean;
  assessmentSessionId?: string | null;
  status?: string | null;
}): RsvpLinkState => {
  // Either credential is enough; the server's submitAttendanceResponse ACL
  // accepts an anonymous token OR a PARENT JWT that owns the player.
  if ((!token && !hasParentAuth) || !assessmentSessionId) return 'malformed';
  if (status === 'attending' || status === 'not_attending') return 'confirm';
  return 'pick';
};
